import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { platformUsersApi } from './api';
import type {
  CreatePlatformUserPayload,
  PlatformUser,
  PlatformUserListParams,
  PlatformUserListResponse,
  UpdatePlatformUserPayload,
} from '@/types/platform-user';

const ROOT_KEY = ['platform-users'] as const;

export function usePlatformUsers(
  params: PlatformUserListParams,
): UseQueryResult<PlatformUserListResponse> {
  return useQuery({
    queryKey: [
      ...ROOT_KEY,
      params.page ?? 1,
      params.search ?? '',
      params.role ?? 'all',
      params.is_active !== undefined ? String(params.is_active) : 'all',
    ],
    queryFn: () => platformUsersApi.list(params),
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function usePlatformUser(id: string | undefined): UseQueryResult<PlatformUser | null> {
  return useQuery({
    queryKey: [...ROOT_KEY, 'detail', id],
    queryFn: () => (id ? platformUsersApi.getById(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useCreatePlatformUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePlatformUserPayload) => platformUsersApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ROOT_KEY });
    },
  });
}

export function useUpdatePlatformUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdatePlatformUserPayload }) =>
      platformUsersApi.update(id, payload),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ROOT_KEY });
      qc.invalidateQueries({ queryKey: [...ROOT_KEY, 'detail', vars.id] });
    },
  });
}

export function useDeactivatePlatformUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => platformUsersApi.deactivate(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ROOT_KEY });
      qc.invalidateQueries({ queryKey: [...ROOT_KEY, 'detail', id] });
    },
  });
}

export function useReactivatePlatformUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => platformUsersApi.reactivate(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ROOT_KEY });
      qc.invalidateQueries({ queryKey: [...ROOT_KEY, 'detail', id] });
    },
  });
}
